import { ACL_ROLES } from '../constants';

/**
 * Role utility functions for determining user permissions and navigation
 */

/**
 * Get user role information from user object
 * @param {Object} user - User object from Redux state
 * @returns {Object} Role information object
 */
export const getUserRoleInfo = (user) => {
  if (!user?.attributes?.roles) {
    return {
      isRoot: false,
      isAdmin: false,
      isDJ: false,
      isGuest: false,
      roles: [],
      primaryRole: 'guest'
    };
  }

  const roles = user.attributes.roles;
  const isRoot = roles.includes(ACL_ROLES.ROOT);
  const isAdmin = roles.includes(ACL_ROLES.ADMIN);
  const isDJ = roles.includes(ACL_ROLES.DJ);
  const isReseller = roles.some(role => ACL_ROLES.RESELLER.test(role));
  const isGroupAdmin = roles.includes('group_admin');

  const isGuest = roles.includes('guest');

  // Determine primary role (highest priority)
  let primaryRole = 'guest';
  if (isRoot) primaryRole = 'root';
  if (isReseller) primaryRole = 'reseller';
  else if (isGroupAdmin) primaryRole = 'group_admin';
  else if (isAdmin) primaryRole = 'admin';
  else if (isDJ) primaryRole = 'dj';

  return {
    isRoot,
    isAdmin,
    isDJ,
    isGuest,
    isReseller,
    isGroupAdmin,
    roles,
    primaryRole
  };
};

/**
 * Check if user has specific role
 * @param {Object} user - User object
 * @param {string} role - Role to check for
 * @returns {boolean}
 */
export const hasRole = (user, role) => {
  const roleInfo = getUserRoleInfo(user);
  return roleInfo.roles.includes(role);
};

/**
 * Check if user has any of the specified roles
 * @param {Object} user - User object
 * @param {Array} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAnyRole = (user, roles) => {
  const roleInfo = getUserRoleInfo(user);
  return roles.some(role => roleInfo.roles.includes(role));
};

/**
 * Check if user has all of the specified roles
 * @param {Object} user - User object
 * @param {Array} roles - Array of roles to check
 * @returns {boolean}
 */
export const hasAllRoles = (user, roles) => {
  const roleInfo = getUserRoleInfo(user);
  return roles.every(role => roleInfo.roles.includes(role));
};

/**
 * Get navigation menu items based on user role
 * @param {Object} user - User object
 * @returns {Array} Array of menu items
 */
export const getNavigationMenu = (user) => {
  const roleInfo = getUserRoleInfo(user);
  
  if (roleInfo.isRoot) {
    return getRootAdminMenu();
  } else if (roleInfo.isReseller) {
    return getResellerMenu();
  } else if (roleInfo.isGroupAdmin) {
    return getGroupAdminMenu();
  } else if (roleInfo.isAdmin) {
    return getStationAdminMenu();
  } else if (roleInfo.isDJ) {
    return getDJMenu();
  } else {
    return getGuestMenu();
  }
};

/**
 * Get default redirect path based on user role
 * @param {Object} user - User object
 * @returns {string} Default path
 */
export const getDefaultPath = (user) => {
  const roleInfo = getUserRoleInfo(user);
  
  if (roleInfo.isRoot) {
    return '/admin/dashboard/invitations';
  } else if (roleInfo.isReseller) {
    return '/admin/dashboard/invitations';
  } else if (roleInfo.isGroupAdmin) {
    return '/admin/station-groups';
  } else if (roleInfo.isAdmin) {
    return '/admin/dashboard/moderator';
  } else if (roleInfo.isDJ) {
    return '/admin/dashboard/moderator';
  } else {
    return '/admin/dashboard/moderator';
  }
};

/**
 * Check if user can access a specific route
 * @param {Object} user - User object
 * @param {string} route - Route path
 * @returns {boolean}
 */
export const canAccessRoute = (user, route) => {
  const roleInfo = getUserRoleInfo(user);
  
  // Define route permissions
  const routePermissions = {
    '/admin/dashboard/invitations': ['root','reseller'],
    '/admin/station-groups': ['admin'],
    '/admin/dashboard/station-groups': ['admin'],
    '/admin/dashboard/user-search': ['root'],
    '/admin/dashboard/sessions': ['root'],
    '/admin/dashboard/apps': ['root'],
    '/admin/dashboard/account/api-limiting': ['root'],
    '/admin/dashboard/analytics': ['root', 'admin'],
    '/admin/dashboard/moderator': ['root', 'admin', 'dj'],
    '/admin/dashboard/schedule': ['root', 'admin'],
    '/admin/dashboard/team': ['root', 'admin'],
    '/admin/dashboard/users': ['root', 'admin'],
    '/admin/dashboard/admax': ['root', 'admin'],
    '/admin/dashboard/configure': ['root', 'admin'],
    '/admin/dashboard/support': ['root', 'admin', 'dj','reseller'],
    '/admin/dashboard/account': ['root', 'admin', 'dj'],
    '/admin/group-admin/account': ['group_admin'],
    '/admin/group-admin/support': ['group_admin']
  };

  // Find matching route permission (group_admin has same access as admin)
  for (const [routePattern, allowedRoles] of Object.entries(routePermissions)) {
    if (route.startsWith(routePattern)) {
      return allowedRoles.some(role => {
        if (role === 'root') return roleInfo.isRoot;
        if (role === 'reseller') return roleInfo.isReseller;
        if (role === 'admin') return roleInfo.isAdmin || roleInfo.isGroupAdmin;
        if (role === 'dj') return roleInfo.isDJ;
        if (role === 'group_admin') return roleInfo.isGroupAdmin;
        return false;
      });
    }
  }

  // Default: allow access if authenticated
  return true;
};

// Menu definitions
const getRootAdminMenu = () => [
  {
    path: "/admin/dashboard/invitations",
    icon: "InvitationsIcon",
    label: "Invitations",
    role: "root"
  },
  {
    path: "/admin/dashboard/sessions",
    icon: "SessionsIcon", 
    label: "Sessions",
    role: "root"
  },
  {
    path: "/admin/dashboard/apps",
    icon: "AppsIcon",
    label: "Apps", 
    role: "root"
  },
  {
    path: "/admin/dashboard/account/contact",
    icon: "AccountIcon",
    label: "Account",
    role: "root"
  },
  {
    path: "/admin/dashboard/user-search",
    icon: "PeopleAltIcon",
    label: "User Search",
    role: "root"
  }
];

const getResellerMenu = () => [
  {
    path: "/admin/dashboard/invitations",
    icon: "InvitationsIcon",
    label: "Invitations",
    role: "reseller"
  },
  {
    path: "/admin/dashboard/support",
    icon: "HelpOutlineIcon",
    label: "Support",
    role: "admin"
  }
 
];

const getStationAdminMenu = () => [
  {
    path: "/admin/dashboard/analytics",
    icon: "BarChartIcon",
    label: "Analytics",
    role: "admin"
  },
  {
    path: "/admin/dashboard/moderator",
    icon: "ChatIcon",
    label: "Moderator",
    role: "admin"
  },
  {
    path: "/admin/dashboard/schedule",
    icon: "CalendarMonthIcon",
    label: "Schedule",
    role: "admin"
  },
  {
    path: "/admin/dashboard/team",
    icon: "Groups2Icon",
    label: "Team",
    role: "admin"
  },
  {
    path: "/admin/dashboard/users",
    icon: "PeopleAltIcon",
    label: "Users",
    role: "admin"
  },
  {
    path: "/admin/dashboard/admax/ads",
    icon: "AttachMoneyIcon",
    label: "AdMax",
    role: "admin"
  },
  {
    path: "/admin/dashboard/configure/basic",
    icon: "SettingsIcon",
    label: "Configure",
    role: "admin"
  },
  {
    path: "/admin/dashboard/account/contact",
    icon: "AccountIcon",
    label: "Account",
    role: "admin"
  },
  {
    path: "/admin/dashboard/support",
    icon: "HelpOutlineIcon",
    label: "Support",
    role: "admin"
  }
];

const getDJMenu = () => [
  {
    path: "/admin/dashboard/moderator",
    icon: "ChatIcon",
    label: "Moderator",
    role: "dj"
  },
  {
    path: "/admin/dashboard/account/contact",
    icon: "AccountIcon",
    label: "Account",
    role: "dj"
  },
  {
    path: "/admin/dashboard/support",
    icon: "HelpOutlineIcon",
    label: "Support",
    role: "dj"
  }
];

const getGroupAdminMenu = () => [
  {
    path: "/admin/group-admin/account/contact",
    icon: "AccountIcon",
    label: "Account",
    role: "group_admin"
  },
  {
    path: "/admin/group-admin/support",
    icon: "HelpOutlineIcon",
    label: "Support",
    role: "group_admin"
  }
];

const getGuestMenu = () => [
  {
    path: "/admin/dashboard/support",
    icon: "HelpOutlineIcon",
    label: "Support",
    role: "guest"
  }
];
